import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  ArrowUp,
  ArrowDown,
  CornerDownLeft,
  LayoutDashboard,
  Home as HomeIcon,
  Layers,
  Sparkles,
  Users,
  Briefcase,
  Building2,
  ListOrdered,
  CalendarCheck,
  MonitorPlay,
  Mail,
  BarChart3,
  History,
  Settings2,
  FileText,
  User as UserIcon,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { candidateService } from '@core/supabase/services/candidateService';
import { jobService } from '@core/supabase/services/jobService';
import { applicationService } from '@core/supabase/services/applicationService';
import { isTopOverlay } from '@shared/lib/overlayRegistry';

const PAGES: { to: string; label: string; icon: LucideIcon; keywords?: string[] }[] = [
  { to: '/home', label: 'Home', icon: HomeIcon, keywords: ['workspace'] },
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, keywords: ['analytics', 'kpi'] },
  { to: '/applications', label: 'Applications', icon: Layers, keywords: ['pipeline'] },
  { to: '/smart-match', label: 'Smart Match', icon: Sparkles, keywords: ['matching', 'match'] },
  { to: '/candidates', label: 'Candidates', icon: Users },
  { to: '/jobs', label: 'Jobs', icon: Briefcase, keywords: ['positions', 'roles'] },
  { to: '/clients', label: 'Clients', icon: Building2, keywords: ['companies'] },
  { to: '/requisitions', label: 'Requisitions', icon: ListOrdered, keywords: ['reqs'] },
  { to: '/interviews', label: 'Interviews', icon: CalendarCheck },
  { to: '/ai-interviews', label: 'AI interviews', icon: MonitorPlay, keywords: ['ai'] },
  { to: '/emails', label: 'Emails', icon: Mail, keywords: ['inbox'] },
  { to: '/team', label: 'Team', icon: Users, keywords: ['members', 'users', 'rbac'] },
  { to: '/reports', label: 'Reports', icon: BarChart3, keywords: ['analytics'] },
  { to: '/object-history', label: 'Object history', icon: History, keywords: ['audit'] },
  { to: '/settings/profile', label: 'Settings', icon: Settings2 },
];

const IS_MAC =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent);

type Group =
  | { id: 'pages'; label: 'Pages'; items: PageResult[] }
  | { id: 'candidates'; label: 'Candidates'; items: CandidateResult[]; loading: boolean; error?: Error }
  | { id: 'jobs'; label: 'Jobs'; items: JobResult[]; loading: boolean; error?: Error }
  | { id: 'applications'; label: 'Applications'; items: ApplicationResult[]; loading: boolean; error?: Error };

type PageResult = {
  kind: 'page';
  id: string;
  label: string;
  icon: LucideIcon;
  to: string;
};
type CandidateResult = {
  kind: 'candidate';
  id: string;
  label: string;
  sublabel?: string;
  to: string;
};
type JobResult = {
  kind: 'job';
  id: string;
  label: string;
  sublabel?: string;
  to: string;
};
type ApplicationResult = {
  kind: 'application';
  id: string;
  label: string;
  sublabel?: string;
  to: string;
};

type FlatItem =
  | PageResult
  | CandidateResult
  | JobResult
  | ApplicationResult;

const MIN_SEARCH_LENGTH = 2;

function filterPages(query: string): PageResult[] {
  const q = query.trim().toLowerCase();
  const mapped = PAGES.map<PageResult>((p) => ({
    kind: 'page',
    id: `page:${p.to}`,
    label: p.label,
    icon: p.icon,
    to: p.to,
  }));
  if (!q) return mapped;
  return mapped.filter((r, idx) => {
    const src = PAGES[idx];
    if (r.label.toLowerCase().includes(q)) return true;
    return (src.keywords ?? []).some((k) => k.toLowerCase().includes(q));
  });
}

export function CommandPalette({
  open,
  onClose,
  query,
  setQuery,
}: {
  open: boolean;
  onClose: () => void;
  query: string;
  setQuery: (q: string) => void;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgId = user?.orgId ?? null;
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const trimmed = query.trim();
  const isSearching = trimmed.length >= MIN_SEARCH_LENGTH;

  const candidatesQuery = useQuery({
    queryKey: ['command-palette', 'candidates', orgId, trimmed],
    enabled: open && Boolean(orgId) && isSearching,
    queryFn: async () => {
      if (!orgId) return [] as CandidateResult[];
      const result = await candidateService.list(orgId, { search: trimmed, pageSize: 8, page: 1 });
      const rows = Array.isArray(result) ? result : result.data;
      return rows.map<CandidateResult>((c) => ({
        kind: 'candidate',
        id: `cand:${c.id}`,
        label: c.full_name || c.email || 'Untitled candidate',
        sublabel: [c.current_title, c.current_company].filter(Boolean).join(' · ') || undefined,
        to: `/candidates/${c.id}`,
      }));
    },
    staleTime: 30_000,
  });

  const jobsQuery = useQuery({
    queryKey: ['command-palette', 'jobs', orgId, trimmed],
    enabled: open && Boolean(orgId) && isSearching,
    queryFn: async () => {
      if (!orgId) return [] as JobResult[];
      const result = await jobService.list(orgId, { search: trimmed, pageSize: 8, page: 1 });
      const rows = Array.isArray(result) ? result : result.data;
      return rows.map<JobResult>((j) => ({
        kind: 'job',
        id: `job:${j.id}`,
        label: j.title || 'Untitled job',
        sublabel:
          (j as { client?: { name?: string } }).client?.name ||
          (j.status ? `Status: ${j.status}` : undefined),
        to: `/jobs/${j.id}`,
      }));
    },
    staleTime: 30_000,
  });

  // Applications: no server-side `search` param — fetch a capped slice and filter by candidate name.
  // TODO(§2.15-v2): add a `search` option on applicationService.list that filters by candidate full_name.
  const applicationsQuery = useQuery({
    queryKey: ['command-palette', 'applications', orgId, trimmed],
    enabled: open && Boolean(orgId) && isSearching,
    queryFn: async () => {
      if (!orgId) return [] as ApplicationResult[];
      const rows = await applicationService.list(orgId, { limit: 40 });
      const q = trimmed.toLowerCase();
      const filtered = rows
        .filter((a) => {
          const name = a.candidate?.full_name?.toLowerCase() ?? '';
          const title = a.job?.title?.toLowerCase() ?? '';
          return name.includes(q) || title.includes(q);
        })
        .slice(0, 8);
      return filtered.map<ApplicationResult>((a) => ({
        kind: 'application',
        id: `app:${a.id}`,
        label: a.candidate?.full_name || a.candidate?.email || 'Application',
        sublabel: [a.job?.title, a.status].filter(Boolean).join(' · ') || undefined,
        to: `/applications/${a.id}`,
      }));
    },
    staleTime: 30_000,
  });

  const groups: Group[] = useMemo(() => {
    const pageItems = filterPages(trimmed);
    const gs: Group[] = [{ id: 'pages', label: 'Pages', items: pageItems }];
    if (isSearching) {
      gs.push({
        id: 'candidates',
        label: 'Candidates',
        items: candidatesQuery.data ?? [],
        loading: candidatesQuery.isLoading || candidatesQuery.isFetching,
        error: candidatesQuery.error as Error | undefined,
      });
      gs.push({
        id: 'jobs',
        label: 'Jobs',
        items: jobsQuery.data ?? [],
        loading: jobsQuery.isLoading || jobsQuery.isFetching,
        error: jobsQuery.error as Error | undefined,
      });
      gs.push({
        id: 'applications',
        label: 'Applications',
        items: applicationsQuery.data ?? [],
        loading: applicationsQuery.isLoading || applicationsQuery.isFetching,
        error: applicationsQuery.error as Error | undefined,
      });
    }
    return gs;
  }, [
    trimmed,
    isSearching,
    candidatesQuery.data,
    candidatesQuery.isLoading,
    candidatesQuery.isFetching,
    candidatesQuery.error,
    jobsQuery.data,
    jobsQuery.isLoading,
    jobsQuery.isFetching,
    jobsQuery.error,
    applicationsQuery.data,
    applicationsQuery.isLoading,
    applicationsQuery.isFetching,
    applicationsQuery.error,
  ]);

  const flatItems: FlatItem[] = useMemo(() => {
    const out: FlatItem[] = [];
    for (const g of groups) {
      for (const it of g.items) out.push(it);
    }
    return out;
  }, [groups]);

  useLayoutEffect(() => {
    setActiveIdx(0);
  }, [trimmed, open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 20);
    return () => window.clearTimeout(t);
  }, [open]);

  const selectAt = useCallback(
    (idx: number) => {
      const item = flatItems[idx];
      if (!item) return;
      navigate(item.to);
      onClose();
    },
    [flatItems, navigate, onClose],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        // Only close if we're the top of the overlay stack (§20.B: palette
        // and Assistant slide-over can coexist; Esc closes top-most first).
        if (isTopOverlay('command-palette')) {
          e.preventDefault();
          onClose();
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => (flatItems.length === 0 ? 0 : (i + 1) % flatItems.length));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) =>
          flatItems.length === 0 ? 0 : (i - 1 + flatItems.length) % flatItems.length,
        );
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        selectAt(activeIdx);
        return;
      }
    },
    [activeIdx, flatItems.length, onClose, selectAt],
  );

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const totalMatches = flatItems.length;
  const anyLoading =
    ((candidatesQuery.isLoading || candidatesQuery.isFetching) && isSearching) ||
    ((jobsQuery.isLoading || jobsQuery.isFetching) && isSearching) ||
    ((applicationsQuery.isLoading || applicationsQuery.isFetching) && isSearching);

  let runningIdx = 0;

  return (
    <AnimatePresence>
      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
          className="fixed inset-0 z-[200] flex items-start justify-center overflow-hidden px-4 pt-[12vh] sm:pt-[14vh]"
          onKeyDown={onKeyDown}
        >
          <motion.button
            type="button"
            aria-label="Close palette"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex w-full max-w-[min(92vw,640px)] flex-col overflow-hidden rounded-[10px] border vscode-border vscode-bg-elevated shadow-2xl"
          >
            <div className="flex items-center gap-3 border-b vscode-border px-4 py-3">
              <Search className="h-4 w-4 vscode-text-tertiary" aria-hidden />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search candidates, jobs, applications, or pages…"
                className="min-w-0 flex-1 bg-transparent text-[13px] vscode-text-primary placeholder:vscode-text-tertiary outline-none"
                aria-label="Search"
                autoComplete="off"
                spellCheck={false}
              />
              <kbd className="hidden items-center gap-1 rounded border vscode-border px-1.5 py-0.5 text-[11px] font-medium vscode-text-tertiary sm:inline-flex">
                {IS_MAC ? '⌘' : 'Ctrl'} /
              </kbd>
            </div>

            <div
              ref={listRef}
              role="listbox"
              aria-label="Search results"
              className="max-h-[min(60vh,460px)] min-h-0 flex-1 overflow-y-auto overscroll-contain py-1"
            >
                {!isSearching ? (
                  <>
                    <GroupHeader label="Pages" />
                    {groups[0].items.map((it) => {
                      const idx = runningIdx++;
                      return (
                        <PageRow
                          key={it.id}
                          item={it}
                          active={idx === activeIdx}
                          idx={idx}
                          onSelect={() => selectAt(idx)}
                          onHover={() => setActiveIdx(idx)}
                        />
                      );
                    })}
                    <div className="px-4 py-3 text-[11px] vscode-text-tertiary">
                      Start typing to search candidates, jobs and applications.
                    </div>
                  </>
                ) : (
                  <>
                    {groups.map((g) => {
                      if (g.id === 'pages') {
                        if (g.items.length === 0) return null;
                        return (
                          <div key={g.id}>
                            <GroupHeader label={g.label} />
                            {g.items.map((it) => {
                              const idx = runningIdx++;
                              return (
                                <PageRow
                                  key={it.id}
                                  item={it}
                                  active={idx === activeIdx}
                                  idx={idx}
                                  onSelect={() => selectAt(idx)}
                                  onHover={() => setActiveIdx(idx)}
                                />
                              );
                            })}
                          </div>
                        );
                      }
                      const icon =
                        g.id === 'candidates'
                          ? UserIcon
                          : g.id === 'jobs'
                            ? Briefcase
                            : FileText;
                      return (
                        <div key={g.id}>
                          <GroupHeader label={g.label} />
                          {g.error ? (
                            <ErrorRow
                              message={`Could not search ${g.label.toLowerCase()}.`}
                              onRetry={() => {
                                if (g.id === 'candidates') void candidatesQuery.refetch();
                                else if (g.id === 'jobs') void jobsQuery.refetch();
                                else if (g.id === 'applications') void applicationsQuery.refetch();
                              }}
                            />
                          ) : g.loading ? (
                            <SkeletonRows />
                          ) : g.items.length === 0 ? (
                            <EmptyRow />
                          ) : (
                            g.items.map((it) => {
                              const idx = runningIdx++;
                              return (
                                <EntityRow
                                  key={it.id}
                                  icon={icon}
                                  label={it.label}
                                  sublabel={it.sublabel}
                                  categoryLabel={g.label.replace(/s$/, '')}
                                  active={idx === activeIdx}
                                  idx={idx}
                                  onSelect={() => selectAt(idx)}
                                  onHover={() => setActiveIdx(idx)}
                                />
                              );
                            })
                          )}
                        </div>
                      );
                    })}
                    {!anyLoading && totalMatches === 0 ? (
                      <div className="px-4 py-6 text-center text-[13px] vscode-text-tertiary">
                        No results for <span className="vscode-text-secondary">"{trimmed}"</span>.
                      </div>
                    ) : null}
                  </>
                )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t vscode-border px-4 py-2 text-[11px] vscode-text-tertiary">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <KbdHint icon={<ArrowUp className="h-3 w-3" aria-hidden />} label="move" />
                <KbdHint icon={<ArrowDown className="h-3 w-3" aria-hidden />} label="move" />
                <KbdHint icon={<CornerDownLeft className="h-3 w-3" aria-hidden />} label="open" />
                <KbdHint label="Esc" suffix="close" />
                <span className="vscode-text-tertiary opacity-50">·</span>
                <span className="hidden sm:inline">
                  <kbd className="inline-flex items-center rounded border vscode-border px-1 py-0.5 text-[11px] vscode-text-tertiary">
                    {IS_MAC ? '⌘' : 'Ctrl'}K
                  </kbd>
                  {' · '}
                  <kbd className="inline-flex items-center rounded border vscode-border px-1 py-0.5 text-[11px] vscode-text-tertiary">
                    {IS_MAC ? '⌘' : 'Ctrl'}/
                  </kbd>
                  {' open'}
                </span>
              </div>
              <div className="hidden sm:block">GoGrabJob · command palette</div>
            </div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}

// ── find mode sub-components ──────────────────────────────────────────────────

function GroupHeader({ label }: { label: string }) {
  return (
    <div className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[0.06em] vscode-text-tertiary">
      {label}
    </div>
  );
}

function PageRow({
  item,
  active,
  idx,
  onSelect,
  onHover,
}: {
  item: PageResult;
  active: boolean;
  idx: number;
  onSelect: () => void;
  onHover: () => void;
}) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      data-idx={idx}
      onMouseEnter={onHover}
      onClick={onSelect}
      className={[
        'flex w-full items-center gap-3 px-4 py-2 text-left text-[13px] transition-colors',
        active
          ? 'bg-[#D1FAE5] text-[#065F46]'
          : 'vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary',
      ].join(' ')}
    >
      <Icon
        className={['h-4 w-4 flex-shrink-0', active ? 'text-[#10B981]' : 'vscode-text-tertiary'].join(' ')}
        aria-hidden
      />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      <span className="hidden shrink-0 text-[11px] uppercase tracking-[0.06em] opacity-60 sm:inline">
        Page
      </span>
    </button>
  );
}

function EntityRow({
  icon: Icon,
  label,
  sublabel,
  categoryLabel,
  active,
  idx,
  onSelect,
  onHover,
}: {
  icon: LucideIcon;
  label: string;
  sublabel?: string;
  categoryLabel: string;
  active: boolean;
  idx: number;
  onSelect: () => void;
  onHover: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      data-idx={idx}
      onMouseEnter={onHover}
      onClick={onSelect}
      className={[
        'flex w-full items-center gap-3 px-4 py-2 text-left text-[13px] transition-colors',
        active
          ? 'bg-[#D1FAE5] text-[#065F46]'
          : 'vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary',
      ].join(' ')}
    >
      <Icon
        className={['h-4 w-4 flex-shrink-0', active ? 'text-[#10B981]' : 'vscode-text-tertiary'].join(' ')}
        aria-hidden
      />
      <span className="min-w-0 flex-1 truncate">
        <span className="block truncate">{label}</span>
        {sublabel ? (
          <span className={['block truncate text-[11px]', active ? 'text-[#065F46]/70' : 'vscode-text-tertiary'].join(' ')}>
            {sublabel}
          </span>
        ) : null}
      </span>
      <span className="hidden shrink-0 text-[11px] uppercase tracking-[0.06em] opacity-60 sm:inline">
        {categoryLabel}
      </span>
    </button>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-1 px-4 py-2" aria-busy="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3 py-1">
          <div className="h-4 w-4 animate-pulse rounded vscode-bg-tertiary" />
          <div className="h-3 flex-1 animate-pulse rounded vscode-bg-tertiary" />
        </div>
      ))}
    </div>
  );
}

function EmptyRow() {
  return <div className="px-4 py-3 text-[11px] vscode-text-tertiary">No matches.</div>;
}

function ErrorRow({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex items-center justify-between gap-2 px-4 py-2 text-[11px] vscode-text-tertiary">
      <span>{message}</span>
      <button
        type="button"
        onClick={onRetry}
        className="rounded border vscode-border px-2 py-0.5 text-[11px] vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary"
      >
        Retry
      </button>
    </div>
  );
}

function KbdHint({ icon, label, suffix }: { icon?: ReactNode; label: string; suffix?: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <kbd className="inline-flex items-center rounded border vscode-border px-1 py-0.5 text-[11px] vscode-text-tertiary">
        {icon ?? label}
      </kbd>
      {icon ? label : suffix ? ` ${suffix}` : null}
    </span>
  );
}
